import { enercityEndpoints } from '@/config/endpoints';
import useAuthentication from '@/hooks/useAuthentication/useAuthentication';
import { BreakPoint } from '@/hooks/useBreakPoint/consts';
import {
  MAIN_HEADER_SESSION_COLLAPSIBLE,
  PREVENT_DISMISS_PROP,
} from '@/hooks/useHeader/consts';
import useIsMobile from '@/hooks/useIsMobile/useIsMobile';
import { LoginPlatformResponse } from '@/hooks/useLogin/types';
import useNavigate from '@/hooks/useNavigate/useNavigate';
import useScroll from '@/hooks/useScroll/useScroll';
import { animated, useSpring, useTrail } from '@react-spring/web';
import clsx from 'clsx';
import React, { CSSProperties } from 'react';
import MainHeader from '../MainHeader/MainHeader';
import styles from './SessionAddon.module.scss';
import { SessionAddonProps } from './types';

const getUserPortalItems = (
  {
    ed4,
    isu,
  }: {
    ed4?: LoginPlatformResponse;
    isu?: LoginPlatformResponse;
  },
  isCollapsibleActive: boolean
) => {
  if (ed4?.token && isu?.token) {
    return [
      <a
        key={enercityEndpoints.enercityPortal}
        className={styles.item}
        href={enercityEndpoints.enercityPortal}
        tabIndex={isCollapsibleActive ? 0 : -1}
      >
        Zum enercityPortal
      </a>,
      <a
        key={enercityEndpoints.servicePortal}
        className={styles.item}
        href={enercityEndpoints.servicePortal}
        tabIndex={isCollapsibleActive ? 0 : -1}
      >
        Zum ServicePortal
      </a>,
    ];
  }

  if (ed4?.token) {
    return [
      <a
        key={enercityEndpoints.enercityPortal}
        className={styles.item}
        href={enercityEndpoints.enercityPortal}
        tabIndex={isCollapsibleActive ? 0 : -1}
      >
        Zum Kundenportal
      </a>,
    ];
  }

  if (isu?.token) {
    return [
      <a
        key={enercityEndpoints.servicePortal}
        className={styles.item}
        href={enercityEndpoints.servicePortal}
        tabIndex={isCollapsibleActive ? 0 : -1}
      >
        Zum Kundenportal
      </a>,
    ];
  }

  return [];
};

const SessionAddon = ({
  isCollapsibleActive,
  isHidden,
  isInverted,
  onCollapsibleToggle,
}: SessionAddonProps) => {
  const isMobile = useIsMobile(BreakPoint.LG);
  const navigate = useNavigate();
  const { isAuthenticated, signOut, user } = useAuthentication();

  useScroll(() => {
    if (isCollapsibleActive) {
      onCollapsibleToggle(MAIN_HEADER_SESSION_COLLAPSIBLE);
    }
  });

  const items = getUserPortalItems(
    user ?? { ed4: undefined, isu: undefined },
    isCollapsibleActive
  ).concat([
    <button
      key="ausloggen"
      className={clsx(styles.item, styles.hasSurrogateText)}
      onClick={() => {
        onCollapsibleToggle(MAIN_HEADER_SESSION_COLLAPSIBLE);
        setTimeout(async () => await signOut(), 500);
      }}
      tabIndex={isCollapsibleActive ? 0 : -1}
    >
      Ausloggen
    </button>,
  ]);

  const dropdownAnimator = useSpring({
    delay: isCollapsibleActive ? 0 : 400,
    config: {
      mass: 1,
      tension: 200,
      friction: 16,
      clamp: !isCollapsibleActive,
    },
    height: isCollapsibleActive
      ? isMobile
        ? items.length * 49 + 15
        : items.length * 40 + 16
      : 0,
    opacity: isCollapsibleActive ? 1 : 0,
    pointerEvents: (isCollapsibleActive
      ? 'all'
      : 'none') as CSSProperties['pointerEvents'],
  });

  const dropdownLinksAnimator = useTrail(items.length, {
    config: {
      mass: 5,
      tension: 2000,
      friction: 100,
      clamp: !isCollapsibleActive,
    },
    delay: isCollapsibleActive ? 200 : 0,
    opacity: isCollapsibleActive ? 1 : 0,
    transform: isCollapsibleActive ? 'translateX(0rem)' : 'translateX(-0.5rem)',
  });

  const handleClick = () => {
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      } else {
        navigate('/login');
      }

      return;
    }

    onCollapsibleToggle(MAIN_HEADER_SESSION_COLLAPSIBLE);
  };

  return (
    <>
      <MainHeader.Addon
        {...(isAuthenticated
          ? {
              'aria-controls': 'main-header-session-menu',
              'aria-expanded': isCollapsibleActive,
              'aria-describedby': isCollapsibleActive
                ? 'Menü schließen'
                : 'Menü öffnen',
            }
          : {})}
        aria-hidden={isHidden}
        className={clsx({
          [styles.isActive]: isCollapsibleActive,
          [styles.isInverted]: isInverted,
        })}
        icon="action/person"
        iconBadgeSlot={isAuthenticated && <CheckmarkBadge />}
        isHidden={isHidden}
        isInverted={isInverted}
        onClick={handleClick}
        title={isAuthenticated ? 'Mein Konto' : 'Login'}
        {...PREVENT_DISMISS_PROP}
      />

      <div
        className={clsx(styles.base, {
          [styles.hasBackdrop]: isCollapsibleActive && isMobile,
        })}
      >
        {isAuthenticated && (
          <animated.div
            aria-hidden={!isCollapsibleActive}
            className={styles.dropdown}
            id="main-header-session-menu"
            style={dropdownAnimator}
            {...PREVENT_DISMISS_PROP}
          >
            {React.Children.map(items, (item, index) => {
              return (
                <animated.li
                  className={styles.itemContainer}
                  style={
                    dropdownLinksAnimator[
                      isCollapsibleActive
                        ? index
                        : dropdownLinksAnimator.length - 1 - index
                    ]
                  }
                  key={`item-${index}`}
                >
                  {item}
                </animated.li>
              );
            })}
          </animated.div>
        )}
      </div>
    </>
  );
};

const CheckmarkBadge = () => (
  <svg
    className={styles.badge}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="12" height="12" rx="6" />
    <path
      d="M3 6.65309L5.12779 8.7022L9 4.5022"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.25"
    />
  </svg>
);

export default SessionAddon;
